import React, { useEffect, useState } from "react";
import moment from "moment";
import { Button, Col, DatePicker, Form, Modal, Row, Select } from "antd";
import { jwtDecode } from "jwt-decode";
import { ReactComponent as WarningIco } from "../../img/warning-icon.svg";
import AuthService from "../../services/AuthService";
import {
  checkIsLoading,
  displayFormat,
  getDictionaryItemById,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../..";
import "./TaskCreator.less";
import { finExportSend } from "../../store/slices/bills";
import { getGlsTasks, resetGlsTasks } from "../../store/slices/glsTasks";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_BILLING_ACCOUNTINGSYSTEM_WRITE } from "../../lib/const/permissions";

const dateFormat = displayFormat;

const layoutCol = {
  wrapperCol: { span: 12 },
  labelCol: { span: 5 },
};

const style = {
  modalHeader: {
    fontSize: "14px",
    color: "#5D6C7A",
  },
  modalSubHeader: {
    fontSize: "14px",
    color: "#5D6C7A",
    label: {
      color: "#000000",
      marginLeft: "5px",
    },
  },
  modalInitiator: {
    fontSize: "14px",
    color: "#5D6C7A",
    label: {
      color: "#000000",
      marginLeft: "5px",
    },
  },
  modalFooter: {
    fontSize: "16px",
    color: "#112244",
  },
};

const TaskCreator = () => {
  const dispatch = useAppDispatch();
  const token = AuthService.getCurrentUser();
  const userName: { display_name: string } = token ? jwtDecode(token) : { display_name: "" };
  const [form] = Form.useForm();
  const { glsTypes, glsStatuses } = useAppSelector((state) => state.rtkDictionaries);
  const { glsTasks } = useAppSelector((state) => state.glsTasks);
  const { finExportSendLoading } = useAppSelector((state) => state.bills);

  const startOfPreviousMonth = moment().subtract(1, "month").startOf("month");
  const endOfPreviousMonth = moment().subtract(1, "month").endOf("month");

  const [activeModal, setActiveModal] = useState(false);

  const onFinish = ({ periodTo, glsTypeIds }) => {
    dispatch(
      getGlsTasks({
        onDate: toHttpFormatEndOfDay(periodTo),
        glsTypeIds,
        glsStatusIds: [1, 2, 3],
      })
    );
    setActiveModal(true);
  };

  const onReset = () => {
    form.resetFields();
    dispatch(resetGlsTasks());
  };

  const onCreateTask = () => {
    setActiveModal(false);
    dispatch(resetGlsTasks());

    dispatch(
      // any потому что не давал добавить аргументы в функцию (так как она создана в js файле)
      (finExportSend as any)({
        glsTypeId: form.getFieldValue("glsTypeIds"),
        periodFrom: toHttpFormatStartOfDay(form.getFieldValue("periodFrom")),
        periodTo: toHttpFormatEndOfDay(form.getFieldValue("periodTo")),
      })
    );
  };

  useEffect(() => {
    return () => {
      onReset();
    };
  }, []);

  const disablePeriodFrom = (date) => {
    const periodTo = form.getFieldValue("periodTo");
    if (!periodTo) return false;
    return date.isAfter(periodTo);
  };

  const disablePeriodTo = (date) => {
    const periodFrom = form.getFieldValue("periodFrom");
    if (!periodFrom) return false;
    return date.isBefore(periodFrom);
  };

  return (
    <>
      <h3 className="task-creator-header">Параметры запуска</h3>
      <Form
        name="dynamic_form_nest_item"
        onFinish={onFinish}
        initialValues={{
          periodFrom: startOfPreviousMonth,
          periodTo: endOfPreviousMonth,
        }}
        form={form}
        autoComplete="off"
      >
        <Row align="top" style={{ paddingBottom: "10%" }}>
          <Col span={12}>
            <Form.Item
              label="Начало периода"
              name="periodFrom"
              labelAlign="left"
              rules={[{ required: true }]}
              {...layoutCol}
            >
              <DatePicker
                allowClear={false}
                style={{ minWidth: "140px" }}
                format={dateFormat}
                disabledDate={disablePeriodFrom}
              />
            </Form.Item>

            <Form.Item
              label="Конец периода"
              name="periodTo"
              labelAlign="left"
              rules={[{ required: true }]}
              {...layoutCol}
            >
              <DatePicker
                allowClear={false}
                style={{ minWidth: "140px" }}
                format={dateFormat}
                disabledDate={disablePeriodTo}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Тип выгрузки"
              name="glsTypeIds"
              labelAlign="left"
              rules={[{ required: true }]}
              {...layoutCol}
              className="form-item-required__hidden-error__mb0"
            >
              <Select
                style={{ width: "240px" }}
                placeholder="Выберите тип выгрузки"
                allowClear={false}
              >
                {glsTypes &&
                  glsTypes.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {hasAccess(MENU_BILLING_ACCOUNTINGSYSTEM_WRITE) && (
          <Row justify="center" style={{ borderTop: "1px solid #CCDDEE", paddingTop: "15px" }}>
            <Button
              type="primary"
              loading={checkIsLoading(finExportSendLoading)}
              onClick={form.submit}
            >
              Создать задание
            </Button>
          </Row>
        )}
      </Form>

      <Modal
        visible={activeModal}
        onCancel={() => {
          setActiveModal(false);
          dispatch(resetGlsTasks());
        }}
        onOk={onCreateTask}
        cancelText="Нет"
        okText="Далее"
        width={540}
        bodyStyle={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          fontSize: "16px",
          paddingTop: "50px",
        }}
      >
        {glsTasks.length > 0 ? (
          <>
            <p style={{ ...style.modalHeader, textAlign: "center" }}>
              <WarningIco style={{ marginBottom: "-8px" }} /> Найдена процедура выгрузки №
              {glsTasks[0]?.id} в статусе&nbsp;
              {getDictionaryItemById(glsStatuses, glsTasks[0]?.glsStatusId)?.name}
              <br />
              (ID задания №{glsTasks[0]?.taskId})
            </p>
            <p style={style.modalHeader}>
              Вы уверены что хотите создать новое задание на выполнение операции?
            </p>
          </>
        ) : (
          <p style={style.modalHeader}>Будет сформировано задание на выполнение операции</p>
        )}

        <p style={style.modalSubHeader}>
          Процедура:
          <span style={style.modalInitiator.label}>
            Выгрузка в бухгалтерию с типом&nbsp;
            {getDictionaryItemById(glsTypes, form.getFieldValue("glsTypeIds"))?.name}
          </span>
        </p>

        <p style={style.modalInitiator}>
          Инициатор:
          <span style={style.modalInitiator.label}>{userName?.display_name}</span>
        </p>

        <p style={style.modalFooter}>Для продолжения нажмите «Далее»</p>
      </Modal>
    </>
  );
};

export default TaskCreator;
